import { AlertTriangle, HelpCircle } from "lucide-react";
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "./Button";
import { Field, Input } from "./Field";
import { Modal } from "./Overlay";
import { IconChip } from "./States";

/** The app's replacement for alert/confirm/prompt (§2.2): promise-returning, styled, focus-
 * trapped. `const ok = await dialogs.confirm({...})`; `const v = await dialogs.prompt({...})`. */

export type ConfirmOptions = { title: string; message?: ReactNode; confirmLabel?: string; cancelLabel?: string; danger?: boolean };
export type PromptOptions = {
  title: string;
  message?: ReactNode;
  label?: string;
  placeholder?: string;
  initialValue?: string;
  confirmLabel?: string;
  inputMode?: "text" | "decimal" | "numeric";
  type?: "text" | "number";
  /** Return an error string to keep the dialog open, or null when the value is acceptable. */
  validate?: (value: string) => string | null;
  /** Red confirm button for a destructive prompt (e.g. discard with a reason). */
  danger?: boolean;
};

type Pending = { id: number } & ({ kind: "confirm"; opts: ConfirmOptions; resolve: (v: boolean) => void } | { kind: "prompt"; opts: PromptOptions; resolve: (v: string | null) => void });
let nextId = 1;

type Api = { confirm: (o: ConfirmOptions) => Promise<boolean>; prompt: (o: PromptOptions) => Promise<string | null> };
const Ctx = createContext<Api | null>(null);

export function useDialogs(): Api {
  const v = useContext(Ctx);
  if (!v) throw new Error("useDialogs must be used inside <DialogsProvider>");
  return v;
}

export function DialogsProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Pending[]>([]);
  const current = queue[0] ?? null;
  const done = useCallback(() => setQueue((q) => q.slice(1)), []);

  const api = useMemo<Api>(
    () => ({
      confirm: (opts) => new Promise<boolean>((resolve) => setQueue((q) => [...q, { id: nextId++, kind: "confirm", opts, resolve }])),
      prompt: (opts) => new Promise<string | null>((resolve) => setQueue((q) => [...q, { id: nextId++, kind: "prompt", opts, resolve }])),
    }),
    [],
  );

  return (
    <Ctx.Provider value={api}>
      {children}
      {current?.kind === "confirm" && (
        <ConfirmView
          key={current.id}
          opts={current.opts}
          onResult={(v) => {
            current.resolve(v);
            done();
          }}
        />
      )}
      {current?.kind === "prompt" && (
        <PromptView
          key={current.id}
          opts={current.opts}
          onResult={(v) => {
            current.resolve(v);
            done();
          }}
        />
      )}
    </Ctx.Provider>
  );
}

function ConfirmView({ opts, onResult }: { opts: ConfirmOptions; onResult: (v: boolean) => void }) {
  const settled = useRef(false);
  const finish = (v: boolean) => {
    if (settled.current) return;
    settled.current = true;
    onResult(v);
  };
  return (
    <Modal open onClose={() => finish(false)} title={opts.title} hideTitle width={420} role={opts.danger ? "alertdialog" : "dialog"} bodyClassName="!pb-0">
      <div className="flex gap-4 pb-1">
        <IconChip icon={opts.danger ? <AlertTriangle /> : <HelpCircle />} tone={opts.danger ? "danger" : "primary"} size={44} />
        <div className="min-w-0 pt-0.5">
          <h2 className="text-lg font-bold text-neutral-900" aria-hidden="true">
            {opts.title}
          </h2>
          {opts.message && <div className="mt-1.5 text-base leading-relaxed text-neutral-600">{opts.message}</div>}
        </div>
      </div>
      <div className="flex justify-end gap-2 py-5">
        <Button variant="secondary" onClick={() => finish(false)}>
          {opts.cancelLabel ?? "Cancel"}
        </Button>
        <Button variant={opts.danger ? "danger" : "primary"} onClick={() => finish(true)} data-autofocus>
          {opts.confirmLabel ?? "Confirm"}
        </Button>
      </div>
    </Modal>
  );
}

function PromptView({ opts, onResult }: { opts: PromptOptions; onResult: (v: string | null) => void }) {
  const [value, setValue] = useState(opts.initialValue ?? "");
  const [error, setError] = useState<string | null>(null);
  const settled = useRef(false);
  const finish = (v: string | null) => {
    if (settled.current) return;
    if (v !== null && opts.validate) {
      const e = opts.validate(v);
      if (e) {
        setError(e);
        return;
      }
    }
    settled.current = true;
    onResult(v);
  };
  return (
    <Modal open onClose={() => finish(null)} title={opts.title} subtitle={opts.message} width={440}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          finish(value);
        }}
      >
        <Field label={opts.label ?? opts.title} error={error} className={opts.label ? undefined : "[&>label]:sr-only"}>
          <Input
            type={opts.type ?? "text"}
            inputMode={opts.inputMode}
            step={opts.type === "number" ? "0.01" : undefined}
            value={value}
            placeholder={opts.placeholder}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            data-autofocus
            inputSize="lg"
            className={opts.type === "number" ? "tnum" : undefined}
          />
        </Field>
        <div className="flex justify-end gap-2 pt-5">
          <Button variant="secondary" onClick={() => finish(null)}>
            Cancel
          </Button>
          <Button type="submit" variant={opts.danger ? "danger" : "primary"}>
            {opts.confirmLabel ?? "OK"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
